import {
  Tabs as MuiTabs,
  TabsProps as MuiTabsProps,
  Tab as MuiTab,
  TabProps as MuiTabProps,
  SxProps,
  Theme,
} from '@mui/material';
import { Box, BoxProps } from '../../Layout/Box';
import { forwardRef, type ReactNode } from 'react';

export interface TabsProps extends MuiTabsProps {
  /**
   * If true, adds a bottom border divider to the tabs container (formStyles pattern).
   * @default false
   */
  withDivider?: boolean;
}

/**
 * Tabs Component
 *
 * A wrapper around MUI Tabs with standardized styling and props.
 * Provides an accessible tabbed interface for organizing content.
 *
 * Includes sharedStyles integration:
 * - `withDivider` prop applies form-tabs-container pattern
 * - Selected tabs automatically get bold font weight (tab-selected pattern)
 *
 * @example
 * ```tsx
 * <Tabs value={activeTab} onChange={handleChange} aria-label="content tabs" withDivider>
 *   <Tab label="Tab 1" />
 *   <Tab label="Tab 2" />
 *   <Tab label="Tab 3" />
 * </Tabs>
 * ```
 */
export const Tabs = forwardRef<HTMLDivElement, TabsProps>(function Tabs(
  {
    value,
    onChange,
    children,
    orientation = 'horizontal',
    variant = 'standard',
    textColor = 'primary',
    indicatorColor = 'primary',
    allowScrollButtonsMobile = false,
    centered = false,
    withDivider = false,
    sx,
    'aria-label': ariaLabel,
    ...other
  },
  ref
) {
  const baseStyles: SxProps<Theme> = {
    ...(withDivider && {
      borderBottom: 2,
      borderColor: 'divider',
    }),
    '& .MuiTab-root.Mui-selected': {
      fontWeight: 800,
    },
    '& .MuiTabs-scrollButtons': {
      width: 48,
      borderRadius: '50%',
    },
  };

  return (
    <MuiTabs
      ref={ref}
      value={value}
      onChange={onChange}
      orientation={orientation}
      variant={variant}
      textColor={textColor}
      indicatorColor={indicatorColor}
      allowScrollButtonsMobile={allowScrollButtonsMobile}
      centered={centered}
      aria-label={ariaLabel}
      sx={[baseStyles, ...(Array.isArray(sx) ? sx : [sx])]}
      {...other}
    >
      {children}
    </MuiTabs>
  );
});

Tabs.displayName = 'Tabs';

export interface TabPanelProps extends Omit<BoxProps, 'ref'> {
  /**
   * The content of the tab panel.
   */
  children?: ReactNode;

  /**
   * The index of the current tab panel. Same type as Tabs value.
   */
  index: MuiTabsProps['value'];

  /**
   * The value of the currently selected tab. Same type as Tabs value.
   */
  value: MuiTabsProps['value'];

  /**
   * Custom styles for the tab panel
   */
  sx?: SxProps<Theme>;

  /**
   * If true, the panel will always be rendered (but hidden when not active).
   * Useful for preserving state across tab switches.
   * @default false
   */
  keepMounted?: boolean;

  /**
   * If true, adds overflow handling for scrollable content (formStyles pattern).
   * Applies overflowY: 'auto' and overflowX: 'hidden'.
   * @default false
   */
  withOverflow?: boolean;

  /**
   * Prefix used to construct the ARIA linkage ids. Must match the prefix
   * supplied to `a11yProps`.
   * @default 'tab'
   */
  idPrefix?: string;
}

/**
 * TabPanel Component
 *
 * A wrapper for tab panel content. Should be used in conjunction with Tabs component.
 * Automatically handles showing/hiding based on the active tab value.
 *
 * @example
 * ```tsx
 * <TabPanel value={activeTab} index={0} withOverflow>
 *   <p>Content for tab 1</p>
 * </TabPanel>
 * ```
 */
export const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(
  (
    {
      children,
      value,
      index,
      sx,
      keepMounted = false,
      withOverflow = false,
      idPrefix = 'tab',
      ...other
    },
    ref
  ) => {
    const isActive = value === index;

    const baseStyles: SxProps<Theme> = {
      ...(withOverflow && {
        overflowY: 'auto',
        overflowX: 'hidden',
      }),
    };

    return (
      <Box
        ref={ref}
        role="tabpanel"
        hidden={!isActive}
        id={`tabpanel-${idPrefix}-${index}`}
        aria-labelledby={`${idPrefix}-${index}`}
        sx={[baseStyles, ...(Array.isArray(sx) ? sx : [sx])]}
        {...other}
      >
        {(keepMounted || isActive) && <Box>{children}</Box>}
      </Box>
    );
  }
);

TabPanel.displayName = 'TabPanel';

/**
 * Helper function to generate accessibility props for tabs.
 * Use this to ensure proper ARIA attributes for keyboard navigation.
 *
 * @param index - The index of the tab
 * @param prefix - Optional prefix for the id (default: 'tab')
 * @returns Object with id and aria-controls attributes
 *
 * @example
 * ```tsx
 * <Tab label="Details" {...a11yProps(0)} />
 * ```
 */
export function a11yProps(index: number | string, prefix = 'tab') {
  return {
    id: `${prefix}-${index}`,
    'aria-controls': `tabpanel-${prefix}-${index}`,
  };
}

export interface TabProps extends MuiTabProps {
  /**
   * The label content for the tab.
   */
  label?: MuiTabProps['label'];

  /**
   * Custom styles for the tab.
   */
  sx?: SxProps<Theme>;
}

/**
 * Tab Component
 *
 * A wrapper around MUI Tab with standardized styling.
 * Includes compact padding and sizing, responsive spacing, and accessibility support.
 *
 * @example
 * ```tsx
 * <Tab label="Details" {...a11yProps(0)} />
 * ```
 */
export const Tab = forwardRef<HTMLDivElement, TabProps>(function Tab({ label, sx, ...other }, ref) {
  const baseStyles: SxProps<Theme> = (theme) => ({
    padding: 0,
    opacity: 1,
    minWidth: 48,
    minHeight: 48,
    fontWeight: theme.typography.fontWeightMedium,
    '&:not(:last-of-type)': {
      marginRight: theme.spacing(3),
      [theme.breakpoints.up('sm')]: {
        marginRight: theme.spacing(5),
      },
    },
    '&:not(.Mui-selected)': {
      color: theme.palette.text.secondary,
    },
  });

  return (
    <MuiTab
      ref={ref}
      label={label}
      disableRipple={true}
      iconPosition="start"
      sx={[baseStyles, ...(Array.isArray(sx) ? sx : [sx])]}
      {...other}
    />
  );
});

Tab.displayName = 'Tab';
